import React, { useState, useMemo, useEffect } from "react";
import ConfirmationPopup from "../../../../common/components/confirmation-popup/confirmation-popup";
import { AlertComponent } from "../../../../common/components";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { CampaignMassDeleteProps } from "../campaign-action-type";
import WarningIcon from "@mui/icons-material/Warning";
import { Typography, Box } from "@mui/material";
import { massDeleteOfCampaigns } from "../../../../api/campaign";
import { getCampaignDetails } from "../../../../api/campaign";

const MassDeleteOfCampaigns: React.FC<CampaignMassDeleteProps> = ({
  campaignIds,
  setSelectedCampaignsMap,
  isOpen,
  setMenuItems,
  selectedActions,
  fetchCampaigns,
  setselectedLeadActionScope,
  handleMassActionMenuClose,
}) => {
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false); // ✅ Loading state
  const [campaignLeads, setCampaignLeads] = useState<
    { campaignId: number; leads: any[] }[]
    >([]);
  
    const fetchLeadsForCampaigns = async (campaignIds: number[]) => {
      setLoading(true);
      try {
        const campaignLeadData = await Promise.all(
          campaignIds.map(async (id) => {
            const response = await getCampaignDetails(id);
            const leads = response?.data?.data?.CampaignLeadAssociation|| [];
            return { campaignId: id, leads: leads }
          })
        );
  
        setLoading(false);
        return campaignLeadData; // This is your final array
      } catch (error: any) {
        setLoading(false);
        setAlertMessage(
          error?.response?.data?.message ||
          "An error occurred while fetching campaign leads."
        );
        setAlertType("error");
        setAlertOpen(true);
        return []; // In case of error, return empty
      }
    };
  

  useEffect(() => {
    if (isOpen && campaignIds?.length > 0) {
      const fetchData = async () => {
        const result = await fetchLeadsForCampaigns(campaignIds);
        setCampaignLeads(result);
      };

      fetchData();
    }
  }, [isOpen, campaignIds]);
  const handleClose = () => {
    setMenuItems((prevItems) =>
      prevItems.map((item) =>
        item.value === selectedActions
          ? { ...item, isOpen: !item.isOpen }
          : item
      )
    );
    handleMassActionMenuClose();
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await massDeleteOfCampaigns({
        campaignIds: campaignIds,
      });
      if (response?.status === 204) {
        setAlertMessage(
          `${campaignIds?.length === 1 ? "Campaign" : "Campaign(s)"} archived successfully.`
        );
        setAlertType("success");
        setSelectedCampaignsMap({});
        setselectedLeadActionScope("");
        fetchCampaigns();
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the lead."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
      setAlertOpen(true);
      handleClose();
    }
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);
  return (
    <>
      {/* {loading && <Loader />}   */}

      <ConfirmationPopup
        open={isOpen}
        title={
          campaignIds?.length === 1 ? "Archive Campaign" : "Archive Campaigns"
        }
        note={
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="body2">
              {campaignLeads?.length > 0 && campaignLeads.some(campaign => campaign?.leads?.length > 0) ? (
                <>
                  Archiving the selected <strong>campaign{campaignIds?.length > 1 ? 's' : ''}</strong> may also remove associated leads, if any. Do you want to proceed?
                </>
              ) : (
                <>
                  Are you sure you want to archive{' '}
                  <strong>the selected campaign{campaignIds?.length > 1 ? 's' : ''}</strong>? This action cannot be undone.
                </>
              )}
            </Typography>

          </Box>
        }
        buttonText="Archive"
        onCancel={handleClose}
        onOkay={handleDelete}
      />

      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
};

export default MassDeleteOfCampaigns;
